import type { Workbook, Worksheet } from 'exceljs';
import type {
  AnalyticsKpiDashboardPayload,
  FeatureAnalyticsPayload,
  RetentionCohort,
  RetentionPayload,
  RetentionSummary,
} from '../../services/analytics';

type WorkbookValue = string | number | boolean | Date | null;

export interface AnalyticsWorkbookExport {
  workbook: Workbook;
  fileName: string;
}

const COLORS = {
  pink: 'FFDB50AC',
  dark: 'FF171717',
  darkAlt: 'FF252525',
  white: 'FFF2F2F2',
  muted: 'FFBFBFBF',
  border: 'FF454545',
};

function asDate(value: string): Date {
  return new Date(`${value.slice(0, 10)}T00:00:00.000Z`);
}

function addSheetTitle(sheet: Worksheet, title: string, start: string, end: string, columns: number): void {
  sheet.mergeCells(1, 1, 1, columns);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 18, color: { argb: COLORS.white } };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.dark } };
  titleCell.alignment = { vertical: 'middle' };
  sheet.getRow(1).height = 30;

  sheet.mergeCells(2, 1, 2, columns);
  const periodCell = sheet.getCell(2, 1);
  periodCell.value = `Period: ${start} to ${end}`;
  periodCell.font = { color: { argb: COLORS.muted }, italic: true };
  periodCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.dark } };
  sheet.getRow(2).height = 22;
  sheet.views = [{ state: 'frozen', ySplit: 2, showGridLines: false }];
}

function addSection(
  sheet: Worksheet,
  title: string,
  headers: string[],
  rows: WorkbookValue[][],
  numberFormats: Record<number, string> = {},
): { headerRow: number; firstDataRow: number; lastDataRow: number } {
  if (sheet.rowCount > 0) sheet.addRow([]);
  const sectionRow = sheet.addRow([title]);
  sheet.mergeCells(sectionRow.number, 1, sectionRow.number, headers.length);
  sectionRow.getCell(1).font = { bold: true, size: 13, color: { argb: COLORS.white } };
  sectionRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.darkAlt } };
  sectionRow.height = 24;

  const headerRow = sheet.addRow(headers);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: COLORS.white } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.pink } };
    cell.alignment = { vertical: 'middle', wrapText: true };
  });
  headerRow.height = 26;

  const firstDataRow = headerRow.number + 1;
  rows.forEach((values) => {
    const row = sheet.addRow(values);
    row.eachCell((cell, columnNumber) => {
      cell.border = { bottom: { style: 'hair', color: { argb: COLORS.border } } };
      cell.alignment = { vertical: 'top', wrapText: false };
      const numberFormat = numberFormats[columnNumber];
      if (numberFormat) cell.numFmt = numberFormat;
    });
  });
  return { headerRow: headerRow.number, firstDataRow, lastDataRow: sheet.rowCount };
}

function finishSheet(sheet: Worksheet, widths: number[]): void {
  widths.forEach((width, index) => { sheet.getColumn(index + 1).width = width; });
  sheet.properties.defaultRowHeight = 19;
  sheet.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 };
}

function addOverviewSheet(workbook: Workbook, data: AnalyticsKpiDashboardPayload): void {
  const sheet = workbook.addWorksheet('Overview');
  addSheetTitle(sheet, 'Ourora Analytics — Overview', data.period.start, data.period.end, 12);

  const summarySection = addSection(sheet, 'Summary', ['Metric', 'Value'], [
    ['Weekly active connected couples', data.summary.weeklyActiveConnectedCouples],
    ['Active meaningful couples', data.summary.activeMeaningfulCouples],
    ['Aggregate days', data.summary.aggregateDays],
    ['Raw events', data.summary.rawEvents],
    ['Meaningful events', data.summary.meaningfulEvents],
    ['Last aggregate computed at', data.summary.lastAggregateComputedAt ? new Date(data.summary.lastAggregateComputedAt) : null],
    ['Aggregate freshness minutes', data.summary.aggregateFreshnessMinutes],
    ['Small-count suppression applied', data.summary.smallCountSuppressionApplied ? 'Yes' : 'No'],
  ]);
  [0, 1, 2, 3, 4, 6].forEach((offset) => {
    sheet.getCell(summarySection.firstDataRow + offset, 2).numFmt = '#,##0';
  });
  sheet.getCell(summarySection.firstDataRow + 5, 2).numFmt = 'yyyy-mm-dd hh:mm';

  const kpiSection = addSection(sheet, 'KPIs', [
    'Group', 'Metric', 'Value', 'Numerator', 'Denominator', 'Cadence', 'Detail', 'Source',
  ], data.groups.flatMap((group) => group.metrics
    .filter((metric) => metric.status === 'ready')
    .map((metric) => [
      group.title, metric.label, metric.value, metric.numerator, metric.denominator,
      metric.cadence, metric.detail, metric.source,
    ])));
  let kpiRowNumber = kpiSection.firstDataRow;
  data.groups.flatMap((group) => group.metrics.filter((metric) => metric.status === 'ready')).forEach((metric) => {
    const cell = sheet.getCell(kpiRowNumber, 3);
    cell.numFmt = metric.format === 'percent' ? '0.0%'
      : metric.format === 'currency' ? '$#,##0.00'
        : metric.format === 'decimal' ? '0.0' : '#,##0';
    kpiRowNumber += 1;
  });

  addSection(sheet, 'Daily Activity', [
    'Date', 'Weekly active connected couples', 'Active couples', 'Meaningful touchpoints',
    'Signups', 'Invites sent', 'Paired couples', 'Paywall views', 'Paid conversions',
    'Sessions', 'Errors', 'Fatal errors',
  ], data.series.map((point) => [
    asDate(point.date), point.weeklyActiveConnectedCouples, point.activeCouples,
    point.meaningfulTouchpoints, point.signups, point.invitesSent, point.pairedCouples,
    point.paywallViews, point.paidConversions, point.sessions, point.errors, point.fatalErrors,
  ]), { 1: 'yyyy-mm-dd', 2: '#,##0', 3: '#,##0', 4: '#,##0', 5: '#,##0', 6: '#,##0', 7: '#,##0', 8: '#,##0', 9: '#,##0', 10: '#,##0', 11: '#,##0', 12: '#,##0' });

  addSection(sheet, 'Surface Breadth', ['Surface', 'Couples', 'Actions', 'Errors'],
    data.surfaces.map((surface) => [surface.surface, surface.couples, surface.actions, surface.errors ?? null]),
    { 2: '#,##0', 3: '#,##0', 4: '#,##0' });
  finishSheet(sheet, [32, 32, 18, 20, 14, 16, 18, 18, 18, 14, 12, 12]);
}

function addFeaturesSheet(workbook: Workbook, data: FeatureAnalyticsPayload): void {
  const sheet = workbook.addWorksheet('Feature Usage');
  addSheetTitle(sheet, 'Ourora Analytics — Feature Usage', data.period.start, data.period.end, 7);
  addSection(sheet, 'Feature Adoption', [
    'Feature', 'Active couples', 'Actions', 'Adoption', 'Repeat rate', 'Error rate', 'Suppressed',
  ], data.features.map((feature) => [
    feature.feature, feature.activeCouples, feature.eventCount, feature.adoption,
    feature.repeatRate, feature.errorRate, feature.suppressed ? 'Yes' : 'No',
  ]), { 2: '#,##0', 3: '#,##0', 4: '0.0%', 5: '0.0%', 6: '0.00%' });
  addSection(sheet, 'Daily Feature Usage', [
    'Date', 'Whispers', 'Whispers media', 'Voice notes', 'Calls', 'Memories', 'Echos',
  ], data.series.map((point) => [
    asDate(point.date), point.whispers, point.whispersMedia, point.voiceNotes,
    point.calls, point.memories, point.echos,
  ]), { 1: 'yyyy-mm-dd', 2: '#,##0', 3: '#,##0', 4: '#,##0', 5: '#,##0', 6: '#,##0', 7: '#,##0' });
  finishSheet(sheet, [20, 18, 18, 16, 16, 16, 14]);
}

function retentionRows(cohorts: RetentionCohort[]): WorkbookValue[][] {
  return cohorts.map((cohort) => [
    asDate(cohort.cohort), cohort.cohortSize, cohort.d1, cohort.d7, cohort.d14,
    cohort.d30, cohort.d60, cohort.d90, cohort.rollingD7 ?? null,
    cohort.rollingD30 ?? null, cohort.suppressed ? 'Yes' : 'No',
  ]);
}

function addRetentionVariant(
  sheet: Worksheet,
  label: string,
  payload: { summary: RetentionSummary; cohorts: RetentionCohort[] },
): void {
  addSection(sheet, `${label} Summary`, ['D1', 'D7', 'D14', 'D30', 'D60', 'D90', 'Rolling D7', 'Rolling D30'], [[
    payload.summary.d1, payload.summary.d7, payload.summary.d14, payload.summary.d30,
    payload.summary.d60, payload.summary.d90, payload.summary.rollingD7 ?? null,
    payload.summary.rollingD30 ?? null,
  ]], { 1: '0.0%', 2: '0.0%', 3: '0.0%', 4: '0.0%', 5: '0.0%', 6: '0.0%', 7: '0.0%', 8: '0.0%' });
  addSection(sheet, `${label} Cohorts`, [
    'Cohort week', 'Couples', 'D1', 'D7', 'D14', 'D30', 'D60', 'D90',
    'Rolling D7', 'Rolling D30', 'Suppressed',
  ], retentionRows(payload.cohorts), {
    1: 'yyyy-mm-dd', 2: '#,##0', 3: '0.0%', 4: '0.0%', 5: '0.0%',
    6: '0.0%', 7: '0.0%', 8: '0.0%', 9: '0.0%', 10: '0.0%',
  });
}

function addRetentionSheet(
  workbook: Workbook,
  retention: RetentionPayload,
  period: { start: string; end: string },
): void {
  const sheet = workbook.addWorksheet('Retention');
  addSheetTitle(sheet, 'Ourora Analytics — Retention', period.start, period.end, 11);
  if (retention.activated) addRetentionVariant(sheet, 'Activated Couples', retention.activated);
  if (retention.paired) addRetentionVariant(sheet, 'Paired Couples', retention.paired);
  if (!retention.activated && !retention.paired) {
    addRetentionVariant(sheet, 'Retention', { summary: retention.summary, cohorts: retention.cohorts });
  }
  finishSheet(sheet, [16, 14, 12, 12, 12, 12, 12, 12, 14, 16, 14]);
}

export async function buildAnalyticsWorkbook(
  overview: AnalyticsKpiDashboardPayload,
  features: FeatureAnalyticsPayload,
  retention: RetentionPayload,
  retentionPeriod: { start: string; end: string },
): Promise<AnalyticsWorkbookExport> {
  const { Workbook: ExcelWorkbook } = await import('exceljs');
  const workbook = new ExcelWorkbook();
  workbook.creator = 'Ourora';
  workbook.company = 'Ourora';
  workbook.created = new Date();
  addOverviewSheet(workbook, overview);
  addFeaturesSheet(workbook, features);
  addRetentionSheet(workbook, retention, retentionPeriod);
  return {
    workbook,
    fileName: `ourora-analytics-${overview.period.start}-${overview.period.end}.xlsx`,
  };
}

export async function downloadAnalyticsWorkbook({ workbook, fileName }: AnalyticsWorkbookExport): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer as BlobPart], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
}
